// ============================================================
//  src/App.js  — updated to include /admin/* routes
// ============================================================
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import AppLayout from './components/layout/AppLayout';

// -- Admin router (self-contained, has its own AuthProvider) -
import AdminRouter from './admin/AdminRouter';
import { ADMIN_BASE } from './admin/adminConfig';

// -- Lazy-load all user-facing pages ------------------------
const LandingPage        = lazy(() => import('./pages/landing/LandingPage'));
const LoginPage          = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage       = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const DashboardPage      = lazy(() => import('./pages/dashboard/DashboardPage'));
const ServicesPage       = lazy(() => import('./pages/dashboard/ServicesPage'));
const AirtimePage        = lazy(() => import('./pages/airtime/AirtimePage'));
const DataPage           = lazy(() => import('./pages/data/DataPage'));
const CableTVPage        = lazy(() => import('./pages/cabletv/CableTVPage'));
const ElectricityPage    = lazy(() => import('./pages/electricity/ElectricityPage'));
const ExamPage           = lazy(() => import('./pages/exam/ExamPage'));
const WalletPage         = lazy(() => import('./pages/wallet/WalletPage'));
const TransferPage       = lazy(() => import('./pages/wallet/TransferPage'));
const TransactionsPage        = lazy(() => import('./pages/transactions/TransactionsPage'));
const TransactionDetailsPage  = lazy(() => import('./pages/transactions/TransactionDetailsPage'));
const ProfilePage        = lazy(() => import('./pages/profile/ProfilePage'));
const KYCPage            = lazy(() => import('./pages/kyc/KYCPage'));
const PinPage            = lazy(() => import('./pages/pin/PinPage'));
const ReferralPage       = lazy(() => import('./pages/referral/ReferralPage'));
const SupportPage        = lazy(() => import('./pages/support/SupportPage'));
const SupportTicketPage  = lazy(() => import('./pages/support/SupportTicketPage'));
const NotificationsPage  = lazy(() => import('./pages/notifications/NotificationsPage'));
const AccountTierPage    = lazy(() => import('./pages/tier/AccountTierPage'));

// -- Full-page spinner while chunks load -------------------
function FullPageSpinner() {
  return (
    <div className="page-loader" style={{ minHeight: '100vh' }}>
      <span className="spinner spinner-dark" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  );
}

// -- Route Guards ------------------------------------------
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

function LandingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

// -- App Shell ---------------------------------------------
function AppShell() {
  return (
    <BrowserRouter>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: '13.5px',
            fontWeight: 600,
            borderRadius: '12px',
            maxWidth: '360px',
          },
        }}
      />
      <Suspense fallback={<FullPageSpinner />}>
        <Routes>
          {/* ── Landing ---------------------------------- */}
          <Route path="/" element={<LandingRoute />} />

          {/* ── Public Auth ------------------------------ */}
          <Route path="/login"           element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

          {/* ── Protected (inside AppLayout) ------------- */}
          <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
            <Route path="dashboard"    element={<DashboardPage />} />
            <Route path="services"     element={<ServicesPage />} />
            <Route path="airtime"      element={<AirtimePage />} />
            <Route path="data"         element={<DataPage />} />
            <Route path="cable-tv"     element={<CableTVPage />} />
            <Route path="electricity"  element={<ElectricityPage />} />
            <Route path="exam"         element={<ExamPage />} />
            <Route path="wallet"       element={<WalletPage />} />
            <Route path="transfer"     element={<TransferPage />} />
            <Route path="transactions"         element={<TransactionsPage />} />
            <Route path="transaction-details"  element={<TransactionDetailsPage />} />
            <Route path="profile"      element={<ProfilePage />} />
            <Route path="kyc"          element={<KYCPage />} />
            <Route path="pin"          element={<PinPage />} />
            <Route path="referral"     element={<ReferralPage />} />
            <Route path="support"      element={<SupportPage />} />
            <Route path="support/:id"  element={<SupportTicketPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="tier" element={<AccountTierPage />} />
          </Route>

          {/* ── Admin (self-contained, /admin/*) --------- */}
          <Route path={`${ADMIN_BASE}/*`} element={<AdminRouter />} />

          {/* ── Fallback ---------------------------------- */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <AppShell />
      </SiteSettingsProvider>
    </AuthProvider>
  );
}